"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export interface SimpleWaveformProps {
  /** Number of bars to display */
  barCount?: number;
  /** Progress value from 0 to 1 */
  progress?: number;
  /** Height of the waveform */
  height?: number;
  /** Color for unplayed bars */
  waveColor?: string;
  /** Color for played bars */
  progressColor?: string;
  /** Callback when user seeks (receives progress 0-1) */
  onSeek?: (progress: number) => void;
  /** Callback during active scrubbing for real-time feedback */
  onScrub?: (progress: number) => void;
  /** Additional class names */
  className?: string;
}

// Seeded random for consistent waveform generation
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const generateBars = (count: number, seed = 42) => {
  const bars: { id: string; height: number }[] = [];
  for (let i = 0; i < count; i++) {
    const random = seededRandom(seed + i);
    const wave = Math.sin((i / count) * Math.PI * 3) * 0.15;
    const barHeight = Math.min(0.9, Math.max(0.2, 0.3 + random * 0.5 + wave));
    bars.push({ id: `bar-${seed}-${i}`, height: barHeight });
  }
  return bars;
};

export const SimpleWaveform = ({
  barCount = 50,
  progress = 0,
  height = 32,
  waveColor = "#E5E7EB",
  progressColor = "#2563EB",
  onSeek,
  onScrub,
  className,
}: SimpleWaveformProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bars] = useState(() => generateBars(barCount));
  const [isDragging, setIsDragging] = useState(false);
  const [localProgress, setLocalProgress] = useState(progress);
  const rafRef = useRef<number | null>(null);
  const lastProgressRef = useRef(progress);

  // Sync external progress when not dragging
  useEffect(() => {
    if (!isDragging) {
      setLocalProgress(progress);
      lastProgressRef.current = progress;
    }
  }, [progress, isDragging]);

  const calculateProgress = useCallback((clientX: number) => {
    if (!containerRef.current) {
      return null;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }, []);

  const handleInteraction = useCallback(
    (clientX: number, isFinal = false) => {
      const newProgress = calculateProgress(clientX);
      if (newProgress === null) {
        return;
      }

      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use requestAnimationFrame for smooth visual updates
      rafRef.current = requestAnimationFrame(() => {
        setLocalProgress(newProgress);
        lastProgressRef.current = newProgress;

        if (isFinal) {
          onSeek?.(newProgress);
        } else {
          onScrub?.(newProgress);
        }
      });
    },
    [calculateProgress, onSeek, onScrub],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onSeek && !onScrub) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      handleInteraction(e.clientX);
    },
    [onSeek, onScrub, handleInteraction],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!onSeek && !onScrub) {
        return;
      }
      e.stopPropagation();
      setIsDragging(true);
      if (e.touches[0]) {
        handleInteraction(e.touches[0].clientX);
      }
    },
    [onSeek, onScrub, handleInteraction],
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleInteraction(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) {
        handleInteraction(e.touches[0].clientX);
      }
    };

    const handleEnd = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      setIsDragging(false);

      // Cancel any pending RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Commit the final position
      onSeek?.(lastProgressRef.current);
    };

    // Use passive: false for touch events to allow preventDefault
    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleEnd);
    document.addEventListener("touchcancel", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleEnd);
      document.removeEventListener("touchcancel", handleEnd);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isDragging, handleInteraction, onSeek]);

  // Cleanup RAF on unmount
  useEffect(
    () => () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [],
  );

  const isInteractive = Boolean(onSeek || onScrub);
  const displayProgress = localProgress;

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex w-full touch-none select-none items-center justify-between",
        isInteractive && "cursor-pointer",
        isDragging && "cursor-grabbing",
        className,
      )}
      style={{ height }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      role={isInteractive ? "slider" : "img"}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(displayProgress * 100)}
      aria-label="Audio waveform"
      tabIndex={isInteractive ? 0 : undefined}
    >
      {bars.map((bar, index) => {
        const barProgress = (index + 0.5) / barCount;
        const isPlayed = barProgress <= displayProgress;
        return (
          <div
            key={bar.id}
            className="rounded-full"
            style={{
              height: `${bar.height * 100}%`,
              width: 3,
              backgroundColor: isPlayed ? progressColor : waveColor,
            }}
          />
        );
      })}
    </div>
  );
};

export interface AudioWaveformProps {
  /** Audio source URL for waveform analysis */
  src?: string;
  /** Number of bars to display */
  barCount?: number;
  /** Progress value from 0 to 1 */
  progress?: number;
  /** Height of the waveform */
  height?: number;
  /** Color for unplayed bars */
  waveColor?: string;
  /** Color for played bars */
  progressColor?: string;
  /** Callback when user seeks (receives progress 0-1) */
  onSeek?: (progress: number) => void;
  /** Callback during active scrubbing */
  onScrub?: (progress: number) => void;
  /** Callback when waveform data is ready */
  onReady?: (duration: number) => void;
  /** Additional class names */
  className?: string;
}

// Analyze audio and extract waveform data
const analyzeAudio = async (
  src: string,
  barCount: number,
): Promise<{ peaks: number[]; duration: number }> => {
  const audioContext = new AudioContext();

  try {
    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const channelData = audioBuffer.getChannelData(0);
    const samplesPerBar = Math.floor(channelData.length / barCount);
    const peaks: number[] = [];

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = Math.min(start + samplesPerBar, channelData.length);

      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += Math.abs(channelData[j]);
      }
      const average = sum / (end - start);
      peaks.push(average);
    }

    // Normalize peaks
    const maxPeak = Math.max(...peaks);
    const normalizedPeaks = peaks.map(p => (maxPeak > 0 ? p / maxPeak : 0));

    // Scale to reasonable visual range
    const scaledPeaks = normalizedPeaks.map(p => 0.15 + p * 0.75);

    await audioContext.close();

    return { peaks: scaledPeaks, duration: audioBuffer.duration };
  } catch (error) {
    await audioContext.close();
    throw error;
  }
};

export const AudioWaveform = ({
  src,
  barCount = 50,
  progress = 0,
  height = 32,
  waveColor = "#E5E7EB",
  progressColor = "#2563EB",
  onSeek,
  onScrub,
  onReady,
  className,
}: AudioWaveformProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bars, setBars] = useState<{ id: string; height: number }[]>(() => generateBars(barCount));
  const [isLoading, setIsLoading] = useState(!!src);
  const [isDragging, setIsDragging] = useState(false);
  const [localProgress, setLocalProgress] = useState(progress);
  const rafRef = useRef<number | null>(null);
  const lastProgressRef = useRef(progress);

  // Load and analyze audio
  useEffect(() => {
    if (!src) {
      setBars(generateBars(barCount));
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    analyzeAudio(src, barCount)
      .then(({ peaks, duration }) => {
        if (cancelled) {
          return;
        }
        setBars(peaks.map((peakHeight, i) => ({ id: `real-${i}`, height: peakHeight })));
        setIsLoading(false);
        onReady?.(duration);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setBars(generateBars(barCount));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [src, barCount, onReady]);

  // Sync external progress when not dragging
  useEffect(() => {
    if (!isDragging) {
      setLocalProgress(progress);
      lastProgressRef.current = progress;
    }
  }, [progress, isDragging]);

  const calculateProgress = useCallback((clientX: number) => {
    if (!containerRef.current) {
      return null;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }, []);

  const handleInteraction = useCallback(
    (clientX: number, isFinal = false) => {
      const newProgress = calculateProgress(clientX);
      if (newProgress === null) {
        return;
      }

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        setLocalProgress(newProgress);
        lastProgressRef.current = newProgress;

        if (isFinal) {
          onSeek?.(newProgress);
        } else {
          onScrub?.(newProgress);
        }
      });
    },
    [calculateProgress, onSeek, onScrub],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onSeek && !onScrub) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      handleInteraction(e.clientX);
    },
    [onSeek, onScrub, handleInteraction],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!onSeek && !onScrub) {
        return;
      }
      e.stopPropagation();
      setIsDragging(true);
      if (e.touches[0]) {
        handleInteraction(e.touches[0].clientX);
      }
    },
    [onSeek, onScrub, handleInteraction],
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleInteraction(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) {
        handleInteraction(e.touches[0].clientX);
      }
    };

    const handleEnd = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      onSeek?.(lastProgressRef.current);
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleEnd);
    document.addEventListener("touchcancel", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleEnd);
      document.removeEventListener("touchcancel", handleEnd);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isDragging, handleInteraction, onSeek]);

  useEffect(
    () => () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [],
  );

  const isInteractive = Boolean(onSeek || onScrub);
  const displayProgress = localProgress;

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex w-full touch-none select-none items-center justify-between",
        isInteractive && "cursor-pointer",
        isDragging && "cursor-grabbing",
        isLoading && "opacity-50",
        className,
      )}
      style={{ height }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      role={isInteractive ? "slider" : "img"}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(displayProgress * 100)}
      aria-label="Audio waveform"
      tabIndex={isInteractive ? 0 : undefined}
    >
      {bars.map((bar, index) => {
        const barProgress = (index + 0.5) / bars.length;
        const isPlayed = barProgress <= displayProgress;
        return (
          <div
            key={bar.id}
            className="rounded-full"
            style={{
              height: `${bar.height * 100}%`,
              width: 3,
              backgroundColor: isPlayed ? progressColor : waveColor,
            }}
          />
        );
      })}
    </div>
  );
};

export default SimpleWaveform;
